import { ButtonBase } from '@material-ui/core';
import cx from 'classnames';
import PropTypes from 'prop-types';

import { ButtonText } from 'src/components/ButtonText';
import { CUSTOM_BUTTON_SIZE } from 'src/components/CustomButton';
import { CustomTag } from 'src/components/CustomTag';
import { CustomTooltip } from 'src/components/CustomTooltip';
import { CustomTypography, TYPOGRAPHY_VARIANT } from 'src/components/CustomTypography';
import { PresetToneOfVoiceIcon } from 'src/components/PresetToneOfVoiceIcon';
import { TruncatedTypography } from 'src/components/TruncatedTypography';
import { getToneOfVoiceLabel, PRESET_TONE_OF_VOICE } from 'src/data/toneOfVoice';

import { useStyles } from './useStyles';

const getLabelHint = (presetTov) => {
  const label = getToneOfVoiceLabel(presetTov);
  const lastCommaIndex = label.lastIndexOf(',');

  if (lastCommaIndex !== -1) {
    return label.substring(0, lastCommaIndex) + ' and' + label.substring(lastCommaIndex + 1);
  } else {
    return label;
  }
};

export const ToneOfVoiceCard = ({
  className,
  classes,
  title,
  description,
  onClick,
  selected,
  disabled,
  label,
  presetTov,
  Icon,
  tag,
  onEditDraft,
  onSaveDraft,
}) => {
  const styles = useStyles({ classes });

  return (
    <ButtonBase
      className={cx(styles.root, className, {
        [styles.selected]: selected,
      })}
      onClick={onClick}
      disabled={disabled}
      data-testid="ToneOfVoiceItem"
    >
      {disabled && <div className={styles.disabledOverlay}></div>}
      <div className={styles.titleWrapper}>
        {Icon ? (
          <Icon className={styles.presetTovIcon} />
        ) : (
          <PresetToneOfVoiceIcon
            className={styles.presetTovIcon}
            presetTov={presetTov}
          />
        )}

        <TruncatedTypography
          className={styles.title}
          variant={TYPOGRAPHY_VARIANT.medium14}
        >
          {title}{' '}
          {onEditDraft && (
            <CustomTag
              className={styles.draftTag}
              tag="Draft"
            />
          )}
        </TruncatedTypography>
      </div>

      {description && (
        <TruncatedTypography
          className={styles.description}
          variant={TYPOGRAPHY_VARIANT.paragraph12}
        >
          {description}
        </TruncatedTypography>
      )}

      {onEditDraft && (
        <div className={styles.draftActions}>
          <ButtonText
            className={styles.actionButton}
            onClick={onEditDraft}
            size={CUSTOM_BUTTON_SIZE.smaller}
          >
            Edit
          </ButtonText>
          <CustomTypography
            className={styles.bullet}
            variant={TYPOGRAPHY_VARIANT.regular12}
          >
            •
          </CustomTypography>
          <ButtonText
            className={styles.actionButton}
            onClick={onSaveDraft}
            size={CUSTOM_BUTTON_SIZE.smaller}
          >
            Save
          </ButtonText>
        </div>
      )}

      {presetTov ? (
        <CustomTooltip
          title={`This tone of voice is designed to match the writing style of: ${getLabelHint(
            presetTov
          )}`}
          classes={{ tooltip: styles.tooltip }}
        >
          <CustomTypography
            className={cx(styles.label, styles.presetLabel)}
            variant={TYPOGRAPHY_VARIANT.regular12}
          >
            {label}
          </CustomTypography>
        </CustomTooltip>
      ) : (
        label && (
          <div className={styles.footer}>
            <TruncatedTypography
              className={styles.label}
              variant={TYPOGRAPHY_VARIANT.regular12}
            >
              {label}
            </TruncatedTypography>

            {tag && (
              <CustomTag
                className={styles.customModelTag}
                tag={tag}
              />
            )}
          </div>
        )
      )}
    </ButtonBase>
  );
};

ToneOfVoiceCard.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.shape({}),
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  onClick: PropTypes.func.isRequired,
  selected: PropTypes.bool,
  disabled: PropTypes.bool,
  label: PropTypes.string,
  presetTov: PropTypes.oneOf(Object.values(PRESET_TONE_OF_VOICE)),
  Icon: PropTypes.elementType,
  tag: PropTypes.string,
  onEditDraft: PropTypes.func,
  onSaveDraft: PropTypes.func,
};
ToneOfVoiceCard.defaultProps = {
  className: null,
  classes: null,
  description: '',
  selected: false,
  disabled: false,
  label: '',
  presetTov: null,
  Icon: null,
  tag: null,
  onEditDraft: null,
  onSaveDraft: null,
};
